import {Component, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {BaseConfigurableComponent} from '@twpub/shared/components/base';
import {FieldProps} from '@twpub/core/models';
import {MatOption, MatSelect, MatSelectTrigger} from '@angular/material/select';
import {ClientConfigurationScheme, ComponentConfiguration, ComponentInit, InitialConfig} from '@twpub/core/utils';
import {ComponentCategory, ControlType} from '@twpub/core/enums';
import {DomainSelector} from '@twpub/core/models/wrappers/domain-selector';
import {Domain} from '@twpub/core/models/domain';
import {DomainTree} from '@twpub/core/models/domain-tree';
import {NgClass} from '@angular/common';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatTooltip} from '@angular/material/tooltip';

/**
 * Displays current domain selection as a dropdown. Only a single domain can be selected at a time, or implicitly all
 * subdomains for a single domain.
 */
@Component({
  selector: 'pub-domain-dropdown',
  templateUrl: './domain-dropdown.component.html',
  styleUrls: ['./domain-dropdown.component.scss'],
  standalone: true,
  imports: [MatFormField, NgClass, MatLabel, MatSelect, MatSelectTrigger, MatOption, MatIcon, MatTooltip]
})
export class DomainDropdownComponent extends BaseConfigurableComponent implements DomainSelector {
  static readonly compName = 'DomainDropdownComponent';

  @Input() currentDomainIds?: number[] = []
  @Input() domainTree!: DomainTree
  domains?: Domain[]
  currentDomain?: Domain;
  /**
   * The domain the user is currently browsing inside the open panel. Separate
   * from currentDomain so drilling into a subdomain does not commit a selection.
   * Reset to currentDomain whenever the committed selection changes.
   */
  viewDomain?: Domain;
  /** Whether the focus row's children are shown. Toggled by its right chevron. */
  expanded = true;
  allLabel: string = $localize`:@@domain.allSelected:All domains`;
  drillInTitle = $localize`:@@domain.drillIn:Show subdomains`;
  drillUpTitle = $localize`:@@domain.drillUp:Go up one level`;

  @ViewChild('select') select!: MatSelect;

  /**
   * Outputs the selected domain id(s) as an array.
   */
  @Output() selectDomains = new EventEmitter<number[]>()

  /** Maps each displayed domain ID to all original domain IDs that share the same name (case-insensitive) */
  private domainIdAliases = new Map<number, number[]>();

  /** Deduplicated top-level domains, rebuilt on each update */
  private dedupedTopLevel: Domain[] = [];

  static Init = class extends ComponentInit {
    readonly scheme = ClientConfigurationScheme.DEFAULT;
    override props: FieldProps[] = [this.LABEL_FIELD, this.VISIBLE_FIELD,
      {
        key: 'AllLabel',
        label: '"All domains" label',
        controlType: ControlType.Textbox,
        description: 'Label for the "All domains" option'
      }
    ];

    constructor() {
      super(DomainDropdownComponent, DomainDropdownComponent.compName, 'Dropdown List', [ComponentCategory.DomainSelector]);
    }

    createConfig(initialConfig: InitialConfig = undefined) {
      return new ComponentConfiguration<DomainSelector>(DomainDropdownComponent, this.name, initialConfig);
    }
  }

  override doUpdate(): void {
    super.doUpdate();
    this.allLabel = this.config.getString('AllLabel') || $localize`:@@domain.allSelected:All domains`;
    this.domainIdAliases = new Map();
    this.dedupedTopLevel = this.deduplicateDomains(this.domainTree.topLevelDomains);

    const domainId = this.currentDomainIds?.[0];
    this.currentDomain = this.findDisplayDomainForId(this.dedupedTopLevel, domainId);
    this.logger.debug('DomainDropdownComponent.doUpdate:', {currentDom: this.currentDomain})
    if (this.select?.panelOpen) {
      // Panel is closing from a selection; defer view rebuild to onOpenedChange.
      this.pendingRebuild = true;
      return;
    }
    this.applyViewState();
  }

  private rebuildDomainList() {
    const focus = this.viewDomain;
    const dictionaryId = this.domainTree.id || 0;
    const allDomainsEntry = (isParent: boolean): Domain =>
      ({name: this.allLabel, id: 0, children: [], dictionaryId, isParent});
    if (!focus) {
      // Top-level view: show the selectable "All domains" entry, but without
      // isParent (there is nowhere to drill up to from here).
      const list: Domain[] = [allDomainsEntry(false), ...this.dedupedTopLevel];
      // If a subdomain is selected, keep the full path to it visible by
      // injecting the non-root portion of the path underneath the
      // corresponding top-level ancestor.
      const pathTail = this.selectionPathBelowRoot();
      if (pathTail.length) {
        const rootId = pathTail[0].parentId;
        const insertAt = list.findIndex(d => d.id === rootId);
        if (insertAt >= 0) {
          list.splice(insertAt + 1, 0, ...pathTail.map(d => ({...d, isChild: true})));
        }
      }
      this.domains = list;
      return;
    }
    // Build ancestor chain root→immediate-parent, so the full path stays
    // visible while the user browses a deep subdomain.
    const chain: Domain[] = [];
    let ancestorId = focus.parentId;
    while (ancestorId) {
      const ancestor = this.findDisplayDomainForId(this.dedupedTopLevel, ancestorId);
      if (!ancestor) {
        this.logger.warn('Missing domain for id: ' + ancestorId);
        break;
      }
      chain.unshift(ancestor);
      ancestorId = ancestor.parentId;
    }
    const domains: Domain[] = [];
    const immediateParent = chain.length ? chain[chain.length - 1] : undefined;
    const upperAncestors = chain.slice(0, -1);
    let focusIsDrillUpSource = false;
    if (immediateParent) {
      // "All domains" at the very top, plain (selectable); only the immediate
      // parent carries the up-chevron so drill-up goes exactly one level.
      domains.push(allDomainsEntry(false));
      domains.push(...upperAncestors);
      domains.push({...immediateParent, isParent: true});
    } else {
      // focus is at top level — "All domains" stays plain and the first-level
      // focus row itself owns the drill-up chevron.
      domains.push(allDomainsEntry(false));
      focusIsDrillUpSource = true;
    }
    domains.push({...focus, isChild: false, isParent: focusIsDrillUpSource});
    if (this.expanded) {
      focus.children.forEach(domain => domain.isChild = true);
      domains.push(...focus.children);
    } else if (this.currentDomain && this.currentDomain.parentId === focus.id) {
      // Collapsed, but the selected child belongs to focus — keep it visible
      // so the user can see what is actually selected.
      domains.push({...this.currentDomain, isChild: true});
    }
    this.domains = domains;
  }

  /** Human-readable path to the current selection, e.g. "A > B > C". */
  getSelectionPathLabel(): string {
    if (!this.currentDomain) return this.allLabel;
    const names: string[] = [];
    let d: Domain | undefined = this.currentDomain;
    while (d) {
      names.unshift(d.name);
      d = d.parentId
        ? this.findDisplayDomainForId(this.dedupedTopLevel, d.parentId)
        : undefined;
    }
    return names.join(' > ');
  }

  /**
   * Ancestor path of the current selection, excluding the top-level root.
   * Used to keep the full selection path visible when the user drills all
   * the way up to the top-level list.
   */
  private selectionPathBelowRoot(): Domain[] {
    if (!this.currentDomain) return [];
    const path: Domain[] = [];
    let d: Domain | undefined = this.currentDomain;
    while (d) {
      path.unshift(d);
      d = d.parentId
        ? this.findDisplayDomainForId(this.dedupedTopLevel, d.parentId)
        : undefined;
    }
    // Drop the top-level root — it's already rendered by the main list.
    return path.slice(1);
  }

  hasChildren(domain: Domain): boolean {
    return !!domain.children?.length && domain.id !== 0;
  }

  /**
   * True when the committed selection is not represented in the visible
   * `domains` list (e.g. the user drilled up past its ancestry). The template
   * renders a hidden fallback option so mat-select keeps showing the
   * selected name instead of clearing the field.
   */
  needsHiddenSelection(): boolean {
    if (!this.currentDomain) return false;
    const id = this.currentDomain.id;
    return !this.domains?.some(d => d.id === id);
  }

  isFocus(domain: Domain): boolean {
    // The focus row may also carry isParent=true when the focus is a
    // first-level domain (to host the drill-up chevron), so only exclude
    // isChild here — the ancestor/breadcrumb rows with isParent are a
    // different domain id than focus anyway.
    return this.viewDomain?.id === domain.id && !domain.isChild;
  }

  isExpanded(domain: Domain): boolean {
    return this.isFocus(domain) && this.expanded;
  }

  drillUp(event: MouseEvent) {
    // Navigate up one level without closing the panel or committing a
    // selection. Triggered from the up-chevron on the parent row.
    event.stopPropagation();
    event.preventDefault();
    if (!this.viewDomain) return;
    const parentId = this.viewDomain.parentId;
    this.viewDomain = parentId
      ? this.findDisplayDomainForId(this.dedupedTopLevel, parentId)
      : undefined;
    this.rebuildDomainList();
  }

  drillInto(domain: Domain, event: MouseEvent) {
    // Browse into the subdomain without closing the panel or committing a
    // selection. mat-option would otherwise treat the click as a selection
    // change and close the overlay (TPUB-407).
    event.stopPropagation();
    event.preventDefault();
    if (!this.hasChildren(domain)) return;
    if (this.isFocus(domain)) {
      // Clicking the chevron on the focus row toggles expand/collapse.
      this.expanded = !this.expanded;
    } else {
      this.viewDomain = domain;
      this.expanded = true;
    }
    this.rebuildDomainList();
  }

  /**
   * Deduplicates domains by name (case-insensitive) at each level.
   * Domains with the same name are merged: children are combined and all original IDs are tracked.
   */
  private deduplicateDomains(domains: Domain[]): Domain[] {
    const byName = new Map<string, { domain: Domain; ids: number[] }>();

    for (const domain of domains) {
      const key = domain.name.toLowerCase();
      const existing = byName.get(key);
      if (existing) {
        existing.ids.push(domain.id);
        existing.domain = {
          ...existing.domain,
          children: [...existing.domain.children, ...domain.children]
        };
      } else {
        byName.set(key, {
          domain: {...domain, children: [...domain.children]},
          ids: [domain.id]
        });
      }
    }

    const result: Domain[] = [];
    for (const {domain, ids} of byName.values()) {
      domain.children = this.deduplicateDomains(domain.children);
      this.domainIdAliases.set(domain.id, ids);
      result.push(domain);
    }

    return result;
  }

  /** Resolves a displayed domain ID to all original domain IDs with the same name */
  private resolveAliasIds(id: number): number[] {
    return this.domainIdAliases.get(id) || [id];
  }

  /** Finds a display domain where the given ID matches any of its aliases */
  private findDisplayDomainForId(dedupedDomains: Domain[], id?: number): Domain | undefined {
    if (id === undefined) return undefined;
    for (const domain of dedupedDomains) {
      const aliases = this.domainIdAliases.get(domain.id) || [domain.id];
      if (aliases.includes(id)) return domain;
      const found = this.findDisplayDomainForId(domain.children, id);
      if (found) return found;
    }
    return undefined;
  }

  onSelect(selValue: string | string[]) {
    this.logger.trace('DomainDropdownComponent.onSelect:', {selValue})
    if (!this.domains?.length) return;
    let selectedIds: number[];
    if (typeof selValue === 'string') {
      if (selValue === '0') {
        selectedIds = [];
      } else {
        const id = Number.parseInt(selValue);
        selectedIds = this.resolveAliasIds(id);
      }
    } else {
      this.logger.debug('DomainDropdownComponent.onSelect:', {selValue})
      selectedIds = selValue.flatMap(id => this.resolveAliasIds(Number.parseInt(id)));
    }
    const isNoneSelected = selectedIds.length === 0;
    selectedIds = isNoneSelected ? [] : selectedIds;
    this.selectDomains.emit(selectedIds);
  }

  /**
   * Defer option-list rebuilds until the panel has fully closed, otherwise
   * the incoming @Input change (triggered by our own emit) churns the
   * mat-option set during mat-select's close animation and the panel
   * visibly re-opens on first selection.
   */
  onOpenedChange(open: boolean) {
    if (!open && this.pendingRebuild) {
      this.pendingRebuild = false;
      this.applyViewState();
    }
  }

  private pendingRebuild = false;

  private applyViewState() {
    if (this.currentDomain && !this.currentDomain.children?.length && this.currentDomain.parentId) {
      this.viewDomain = this.findDisplayDomainForId(this.dedupedTopLevel, this.currentDomain.parentId);
    } else {
      this.viewDomain = this.currentDomain;
    }
    this.expanded = this.viewDomain?.id === this.currentDomain?.id;
    this.rebuildDomainList();
  }

  getSelectedValue() {
    if (this.currentDomain) return this.currentDomain.id.toString();
    return '0';
  }

  getIconName(domain: Domain) {
    return domain.isParent ? 'keyboard_arrow_up' : '';
  }
}
